import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusCircle, Edit2, Trash2, Save, X, Loader2,
  ChevronUp, ChevronDown,
  Shield, Lock, Globe, FileCheck, CheckCircle,
  Activity, Star, Award, BadgeCheck, ShieldCheck, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';
import ImageUploadField from '../../components/ImageUploadField';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

const ICON_OPTIONS = [
  'Shield', 'Lock', 'Globe', 'FileCheck', 'CheckCircle',
  'Activity', 'Star', 'Award', 'BadgeCheck', 'ShieldCheck',
];

const ICON_MAP = {
  Shield, Lock, Globe, FileCheck, CheckCircle,
  Activity, Star, Award, BadgeCheck, ShieldCheck,
};

function BadgeIcon({ name, size = 20, className = '' }) {
  const IconComp = ICON_MAP[name] || Shield;
  return <IconComp size={size} className={className} />;
}

/* ── Live Badge Preview Card ───────────────────────────────────────── */
function BadgePreviewCard({ form }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: form.icon_background || 'linear-gradient(135deg, #1e2a78, #2d3a8c)' }}>
        <BadgeIcon name={form.icon_name} size={18} style={{ color: form.icon_color || '#fff' }} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-slate-900 truncate">{form.title || 'Badge Title'}</p>
        {form.subtitle && <p className="text-xs text-slate-500 truncate">{form.subtitle}</p>}
      </div>
    </div>
  );
}

/* ── Modal ─────────────────────────────────────────────────────────── */
function BadgeModal({ badge, onClose, onSaved }) {
  const isEdit = Boolean(badge?.id);
  const token = localStorage.getItem('token');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    icon_name: badge?.icon_name || 'Shield',
    title: badge?.title || '',
    subtitle: badge?.subtitle || '',
    icon_background: badge?.icon_background || 'linear-gradient(135deg, #1e2a78, #2d3a8c)',
    icon_color: badge?.icon_color || '#ffffff',
    icon_image_url: badge?.icon_image_url || '',
    sort_order: badge?.sort_order ?? 0,
    is_active: badge?.is_active !== false,
  });

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sort_order: parseInt(form.sort_order) || 0,
        icon_image_url: form.icon_image_url || null,
      };
      const url = isEdit
        ? `${API}/api/marketing/compliance-badges/${badge.id}`
        : `${API}/api/marketing/compliance-badges`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Save failed');
      toast.success(isEdit ? 'Badge updated' : 'Badge created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 shrink-0">
          <h2 className="text-lg font-bold text-slate-900">
            {isEdit ? 'Edit Badge' : 'Add Badge'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Live Preview */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Live Preview</p>
            <BadgePreviewCard form={form} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Icon</label>
              <select value={form.icon_name} onChange={set('icon_name')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30">
                {ICON_OPTIONS.map(ic => (
                  <option key={ic} value={ic}>{ic}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Icon Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.icon_color} onChange={set('icon_color')}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                <input type="text" value={form.icon_color} onChange={set('icon_color')}
                  className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 font-mono" />
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Title *</label>
              <input type="text" value={form.title} onChange={set('title')} required
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder="e.g. NDIS Registered" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Subtitle</label>
              <input type="text" value={form.subtitle} onChange={set('subtitle')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder="e.g. Quality & Safeguards" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Icon Background (CSS gradient or color)</label>
              <input type="text" value={form.icon_background} onChange={set('icon_background')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 font-mono"
                placeholder="linear-gradient(135deg, #1e2a78, #2d3a8c)" />
              <div className="mt-2 h-8 rounded-lg border border-slate-200"
                style={{ background: form.icon_background }} />
            </div>
            <div className="col-span-2">
              <ImageUploadField
                label="Custom icon image (optional — overrides icon name)"
                value={form.icon_image_url || ''}
                onChange={(url) => setForm(f => ({ ...f, icon_image_url: url }))}
                hint="Upload a PNG/SVG to use instead of a Lucide icon"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sort Order</label>
              <input type="number" min={0} value={form.sort_order} onChange={set('sort_order')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div className="flex items-center gap-3 pt-5">
              <input type="checkbox" id="cb_active" checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                className="w-4 h-4 accent-primary-600 rounded" />
              <label htmlFor="cb_active" className="text-sm font-medium text-slate-700">Active</label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Badge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main Page ─────────────────────────────────────────────────────── */
export default function ComplianceBadgesEditor() {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const token = localStorage.getItem('token');

  async function fetchBadges() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/compliance-badges/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setBadges(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchBadges(); }, []);

  async function deleteBadge(id, title) {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      const res = await fetch(`${API}/api/marketing/compliance-badges/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Badge deleted');
      fetchBadges();
    } catch {
      toast.error('Failed to delete');
    }
  }

  async function toggleActive(badge) {
    try {
      await fetch(`${API}/api/marketing/compliance-badges/${badge.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...badge, is_active: !badge.is_active }),
      });
      fetchBadges();
    } catch {
      toast.error('Failed to update');
    }
  }

  async function reorder(index, direction) {
    const arr = [...badges];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= arr.length) return;
    [arr[index], arr[swap]] = [arr[swap], arr[index]];
    setBadges(arr);
    try {
      await Promise.all(
        arr.map((b, i) =>
          fetch(`${API}/api/marketing/compliance-badges/${b.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...b, sort_order: i }),
          })
        )
      );
      fetchBadges();
    } catch {
      toast.error('Failed to reorder');
    }
  }

  return (
    <div className="p-6 space-y-6">
      {modal && (
        <BadgeModal
          badge={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={fetchBadges}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compliance Badges</h1>
          <p className="text-slate-500 text-sm mt-0.5">These badges appear in the Security &amp; Compliance section on the homepage</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${MARKETING_URL}/#compliance`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors">
            <ExternalLink size={14} />
            Preview on Site
          </a>
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm">
            <PlusCircle size={16} />
            Add Badge
          </button>
        </div>
      </div>

      {/* Badge preview grid */}
      {!loading && badges.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Badge Preview</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
            {badges.filter(b => b.is_active).map(badge => (
              <div key={badge.id}
                className="flex flex-col items-center text-center p-4 rounded-xl border border-slate-100 bg-slate-50 gap-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: badge.icon_background || 'linear-gradient(135deg, #1e2a78, #2d3a8c)' }}>
                  <BadgeIcon name={badge.icon_name} size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900 leading-tight">{badge.title}</p>
                  {badge.subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{badge.subtitle}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : badges.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Shield size={40} className="mb-3 opacity-30" />
            <p className="font-medium">No badges yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Badge</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Subtitle</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Icon</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {badges.map((badge, index) => (
                <tr key={badge.id} className={clsx('hover:bg-slate-50 transition-colors', !badge.is_active && 'opacity-50')}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: badge.icon_background || 'linear-gradient(135deg, #1e2a78, #2d3a8c)' }}>
                        <BadgeIcon name={badge.icon_name} size={16} className="text-white" />
                      </div>
                      <span className="text-sm font-semibold text-slate-900">{badge.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm text-slate-500">{badge.subtitle || '—'}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{badge.icon_name}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <button onClick={() => toggleActive(badge)}
                      className={clsx(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors',
                        badge.is_active
                          ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      )}>
                      {badge.is_active ? 'Active' : 'Hidden'}
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => reorder(index, 'up')} disabled={index === 0}
                        className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-30">
                        <ChevronUp size={14} />
                      </button>
                      <button onClick={() => reorder(index, 'down')} disabled={index === badges.length - 1}
                        className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-30">
                        <ChevronDown size={14} />
                      </button>
                      <button onClick={() => setModal(badge)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => deleteBadge(badge.id, badge.title)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
